(() => {
  'use strict';
  if (window.rudcaHeaderShippingReady) return;
  window.rudcaHeaderShippingReady = true;
  const selector = '[data-rudca-header-shipping]';
  const destinationKey = 'rudca:shipping-destination:v1';
  const provinces = new Set('AL AZ AR CA CO CT DE DC FL GA ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' '));
  const provinceNames = new Set('Alabama|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|District of Columbia|Florida|Georgia|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New Hampshire|New Jersey|New Mexico|New York|North Carolina|North Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode Island|South Carolina|South Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West Virginia|Wisconsin|Wyoming'.toLowerCase().split('|'));
  let currentEpoch = -1;
  let currentRevision = -1;
  let settledEpoch = -1;
  let suspended = false;
  let waiting = false;
  let state = null;
  let destination = null;
  let quote = null;
  let quoteRequest = null;
  let quoteVersion = null;
  let moneyFormatter = null;
  let moneyLocale = null;
  let moneyAmount = null;
  let moneyText = null;

  function normalize(value) { return typeof value === 'string' ? value.trim() : ''; }
  function knownDestination(value) {
    if (!value || typeof value.country !== 'string' || typeof value.province !== 'string') return null;
    if (value.country.length > 100 || value.province.length > 100) return null;
    return {country: normalize(value.country), province: normalize(value.province)};
  }
  function destinationSupported(value) {
    if (!value) return true;
    const country = normalize(value.country).toUpperCase();
    const province = normalize(value.province);
    if (country && !['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(country)) return false;
    return !province || provinces.has(province.toUpperCase()) || provinceNames.has(province.toLowerCase());
  }
  function rememberDestination(value) {
    destination = knownDestination(value);
    try {
      if (destination) window.sessionStorage.setItem(destinationKey, JSON.stringify({...destination, quote}));
      else window.sessionStorage.removeItem(destinationKey);
    } catch (_) {}
    render(state);
  }
  function validState(value) {
    if (!value || !Number.isSafeInteger(value.total) || value.total < 0 || !Number.isSafeInteger(value.count) || value.count < 0 || value.count === 0 && value.total !== 0) return null;
    if (!/^[A-Z]{3}$/.test(value.currency || '')) return null;
    return {total: value.total, count: value.count, currency: value.currency, supported: value.supported === true};
  }
  function fromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    if (!cart.items.every(item => Number.isSafeInteger(item.quantity) && item.quantity > 0) || cart.items.reduce((count, item) => count + item.quantity, 0) !== cart.item_count) return null;
    return validState({
      total: cart.total_price,
      count: cart.item_count,
      currency: cart.currency,
      supported: (cart.item_count === 0 || cart.requires_shipping === true) && cart.items.every(item => item.requires_shipping === true && item.gift_card !== true)
    });
  }
  function amount(cents) {
    const locale = document.documentElement.lang || 'en';
    if (moneyLocale === locale && moneyAmount === cents && moneyText !== null) return moneyText;
    try {
      if (!moneyFormatter || moneyLocale !== locale) {
        moneyFormatter = null;
        moneyFormatter = new Intl.NumberFormat(locale, {style: 'currency', currency: 'USD'});
      }
      moneyText = moneyFormatter.format(cents / 100);
    } catch (_) { moneyText = '$' + (cents / 100).toFixed(2); }
    moneyLocale = locale;
    moneyAmount = cents;
    return moneyText;
  }
  function setText(node, value) {
    if (node.textContent !== value) node.textContent = value;
  }
  function setAttribute(node, name, value) {
    if (value === null) {
      if (node.hasAttribute(name)) node.removeAttribute(name);
    } else if (node.getAttribute(name) !== value) node.setAttribute(name, value);
  }
  // Presentation only: authoritative cart state and the existing destination/quote rules.
  const estimatorSelector = '[data-rudca-estimator]';
  const mobileEstimator = window.matchMedia('(max-width: 640px)');
  let estimatorChoice = 'auto';
  let estimatorPanelOpen = true;
  let estimatorCompact = false;
  let estimatorAddress = null; // In-memory only; ZIP is never persisted.
  let restoringEstimator = false;
  const estimatorDesktopState = new WeakMap();
  let observedEstimatorToggle = null;
  const estimatorToggleObserver = new MutationObserver(() => syncEstimatorToggleLabel(observedEstimatorToggle));
  function syncEstimatorToggleLabel(toggle) {
    if (!toggle || !toggle.isConnected) return;
    const host = toggle.closest(estimatorSelector);
    const label = toggle.querySelector('[data-rudca-estimator-toggle-label]');
    if (host && label) setText(label, toggle.getAttribute('aria-expanded') === 'true' ? host.dataset.hideLabel : host.dataset.showLabel);
  }
  function observeEstimatorToggle(toggle) {
    if (toggle === observedEstimatorToggle) return;
    estimatorToggleObserver.disconnect();
    observedEstimatorToggle = toggle;
    if (toggle) estimatorToggleObserver.observe(toggle, {attributes: true, attributeFilter: ['aria-expanded']});
  }
  function estimatorHost() { return document.querySelector(estimatorSelector); }
  function constrainEstimatorCountry() {
    const country = estimatorHost()?.querySelector('[name="country"]');
    if (!country) return;
    const us = Array.from(country.options).find(option => ['US', 'UNITED STATES'].includes(option.value.toUpperCase()));
    if (!us) return;
    const changed = country.value !== us.value;
    Array.from(country.options).forEach(option => { if (option !== us) option.remove(); });
    country.setAttribute('data-default', us.text);
    country.value = us.value;
    if (changed) country.dispatchEvent(new Event('change', {bubbles: true}));
  }
  function captureEstimator() {
    const host = estimatorHost();
    if (!host || restoringEstimator) return;
    const country = host.querySelector('[name="country"]');
    const province = host.querySelector('[name="province"]');
    const zip = host.querySelector('[name="zip"]');
    if (country && province && zip) estimatorAddress = {country: country.value, province: province.value, zip: zip.value};
  }
  function labelEstimator(host) {
    const country = host.querySelector('[name="country"]');
    const province = host.querySelector('[name="province"]');
    const label = host.querySelector('label[for="shipping-estimator-province"]');
    if (!country || !province || !label) return;
    const us = ['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(normalize(country.value).toUpperCase());
    const text = us ? host.dataset.stateLabel : host.dataset.provinceLabel;
    if (!text) return;
    setText(label, text);
    setAttribute(province, 'data-rudca-province-placeholder', '— ' + text + ' —');
    const blank = Array.from(province.options).find(option => option.value === '');
    if (blank) setText(blank, '— ' + text + ' —');
  }
  function restoreEstimator() {
    const host = estimatorHost();
    if (!host) return;
    constrainEstimatorCountry();
    if (estimatorAddress) {
      const country = host.querySelector('[name="country"]');
      const province = host.querySelector('[name="province"]');
      const zip = host.querySelector('[name="zip"]');
      if (country && province && zip) {
        restoringEstimator = true;
        try {
          // The native CountrySelector recreates options on cart:rerendered, even
          // when the controls themselves retain their DOM identity.
          country.value = estimatorAddress.country;
          country.dispatchEvent(new Event('change', {bubbles: true}));
          province.value = estimatorAddress.province;
          province.dispatchEvent(new Event('change', {bubbles: true}));
          zip.value = estimatorAddress.zip;
        } finally { restoringEstimator = false; }
      }
    }
    labelEstimator(host);
  }
  function renderEstimator(value) {
    const host = estimatorHost();
    if (!host) { observeEstimatorToggle(null); return; }
    const summary = host.querySelector('[data-rudca-estimator-summary]');
    const toggle = host.querySelector('[data-action="toggle-collapsible"]');
    const reopen = host.querySelector('[data-rudca-estimator-reopen]');
    const form = host.querySelector('.shipping-estimator');
    if (!summary || !toggle || !reopen || !form) return;
    observeEstimatorToggle(toggle);
    labelEstimator(host);
    setAttribute(summary, 'aria-busy', waiting ? 'true' : null);
    const panel = host.querySelector('#shipping-estimator');
    if (!mobileEstimator.matches) {
      summary.hidden = true;
      setAttribute(host, 'data-rudca-compact', null);
      setAttribute(host, 'data-rudca-panel-open', null);
      const native = estimatorDesktopState.get(host);
      if (native) {
        setAttribute(toggle, 'aria-expanded', native.expanded);
        if (panel) setAttribute(panel, 'aria-hidden', native.hidden);
        estimatorDesktopState.delete(host);
      }
      const desktopLabel = toggle.querySelector('[data-rudca-estimator-toggle-label]');
      if (desktopLabel) setText(desktopLabel, toggle.getAttribute('aria-expanded') === 'true' ? host.dataset.hideLabel : host.dataset.showLabel);
      return;
    }
    if (!estimatorDesktopState.has(host)) estimatorDesktopState.set(host, {
      expanded: toggle.getAttribute('aria-expanded'), hidden: panel && panel.getAttribute('aria-hidden')
    });
    const oldRect = host.getBoundingClientRect();
    const oldCompact = host.getAttribute('data-rudca-compact');
    const oldPanelOpen = host.getAttribute('data-rudca-panel-open');
    if (!waiting) {
      const header = document.querySelector(selector);
      const localDestination = destination || {
        country: header?.dataset.destinationCountry || '', province: header?.dataset.destinationProvince || ''
      };
      const paid = quote && (!value || quote.total === value.total && quote.count === value.count && quote.currency === value.currency);
      const eligible = value && value.count > 0 && value.total >= 6999 && value.currency === 'USD' && value.supported && destinationSupported(localDestination) && !paid;
      // Explicit choices survive cart refreshes. Automatic mode follows eligibility.
      estimatorPanelOpen = estimatorChoice === 'open' || (estimatorChoice === 'auto' && (!eligible || form.contains(document.activeElement)));
      estimatorCompact = !!eligible && !estimatorPanelOpen;
    }
    setAttribute(host, 'data-rudca-compact', String(estimatorCompact));
    setAttribute(host, 'data-rudca-panel-open', String(estimatorPanelOpen));
    const toggleLabel = toggle.querySelector('[data-rudca-estimator-toggle-label]');
    if (toggleLabel) setText(toggleLabel, estimatorPanelOpen ? host.dataset.hideLabel : host.dataset.showLabel);
    summary.hidden = !estimatorCompact;
    setAttribute(reopen, 'aria-expanded', String(estimatorPanelOpen));
    setAttribute(toggle, 'aria-expanded', String(estimatorPanelOpen));
    if (panel) setAttribute(panel, 'aria-hidden', String(!estimatorPanelOpen));
    // Preserve the reading position when this card changes entirely above view.
    // Do not move the page for a visible card or during the first initialization.
    if (oldCompact !== null && (oldCompact !== String(estimatorCompact) || oldPanelOpen !== String(estimatorPanelOpen)) && oldRect.bottom <= 0) {
      const delta = host.getBoundingClientRect().height - oldRect.height;
      if (delta) window.scrollBy({top: delta, left: 0, behavior: 'instant'});
    }
  }

  function render(value) {
    renderEstimator(value);
    document.querySelectorAll(selector).forEach(node => {
      const label = node.querySelector('[data-rudca-header-shipping-message]');
      const progress = node.querySelector('[data-rudca-header-shipping-progress]');
      if (!label || !progress || Number(node.dataset.threshold) !== 6999) return;
      const localDestination = destination || {country: node.dataset.destinationCountry || '', province: node.dataset.destinationProvince || ''};
      let mode = 'generic';
      let text = node.dataset.genericMessage;
      let total = 0;
      const quotedPaid = quote && (!value || quote.total === value.total && quote.count === value.count && quote.currency === value.currency);
      if (!destinationSupported(localDestination) || quotedPaid || value && (value.currency !== 'USD' || !value.supported)) {
        mode = 'unsupported';
        text = node.dataset.unsupportedMessage;
      } else if (value) {
        if (value.count === 0) {
          mode = 'empty';
          text = node.dataset.emptyMessage;
        } else {
          total = Math.min(value.total, 6999);
          mode = value.total >= 6999 ? 'reached' : 'remaining';
          text = mode === 'reached' ? node.dataset.reachedMessage : node.dataset.remainingTemplate.replace('__rudca_amount__', amount(6999 - value.total));
        }
      }
      setText(label, text || '');
      setAttribute(node, 'data-state', mode);
      setAttribute(node, 'data-state-version', currentEpoch + ':' + currentRevision);
      if (progress.max !== 6999) progress.max = 6999;
      if (progress.value !== total) progress.value = total;
      setText(progress, Math.round(total * 100 / 6999) + '%');
      setAttribute(progress, 'aria-hidden', mode === 'generic' || mode === 'unsupported' ? 'true' : null);
      setAttribute(node, 'aria-busy', waiting ? 'true' : null);
      if (value) {
        setAttribute(node, 'data-subtotal', String(value.total));
        setAttribute(node, 'data-count', String(value.count));
        setAttribute(node, 'data-currency', value.currency);
        setAttribute(node, 'data-supported', String(value.supported));
      }
    });
  }
  function begin(event) {
    const epoch = event.detail && event.detail.epoch;
    if (suspended || !Number.isSafeInteger(epoch) || epoch <= currentEpoch) return;
    captureEstimator();
    currentEpoch = epoch;
    currentRevision = -1;
    waiting = true;
    quoteRequest = null;
    state = null;
    render(null);
  }
  function project(event) {
    const detail = event.detail || {};
    if (suspended || !Number.isSafeInteger(detail.epoch) || !Number.isSafeInteger(detail.revision) || detail.epoch < currentEpoch || detail.epoch <= settledEpoch) return;
    if (detail.epoch === currentEpoch && detail.revision < currentRevision) return;
    currentEpoch = detail.epoch;
    currentRevision = detail.revision;
    waiting = true;
    state = validState(detail.projection);
    render(state);
  }
  function settle(event) {
    const detail = event.detail || {};
    if (suspended || !Number.isSafeInteger(detail.epoch) || detail.epoch < currentEpoch || detail.epoch <= settledEpoch || document.documentElement.hasAttribute('data-rudca-cart-pending')) return;
    currentEpoch = detail.epoch;
    settledEpoch = detail.epoch;
    currentRevision = -1;
    waiting = false;
    state = fromCart(detail.cart);
    if (state && state.count === 0) {
      estimatorAddress = null;
      estimatorChoice = 'auto';
      estimatorPanelOpen = true;
      estimatorCompact = false;
    } else restoreEstimator();
    render(state);
  }
  function readDestination(host) {
    if (!host) return;
    const country = host.querySelector('[name="country"]');
    const province = host.querySelector('[name="province"]');
    if (country) {
      const next = {country: country.value, province: province ? province.value : ''};
      if (!destination || next.country !== destination.country || next.province !== destination.province) quote = null;
      quoteRequest = null;
      rememberDestination(next);
    }
  }
  function initialize() {
    const node = document.querySelector(selector);
    if (!node) return;
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(destinationKey) || 'null');
      destination = knownDestination(saved);
      quote = saved && validState(saved.quote);
    } catch (_) {}
    waiting = document.documentElement.hasAttribute('data-rudca-cart-pending');
    state = waiting ? null : validState({total: Number(node.dataset.subtotal), count: Number(node.dataset.count), currency: node.dataset.currency, supported: node.dataset.supported === 'true'});
    render(state);
  }
  document.addEventListener('rudca:cart-started', begin);
  document.addEventListener('rudca:cart-projected', project);
  document.addEventListener('rudca:cart-settled', settle);
  document.addEventListener('rudca:shipping-quote-started', event => {
    const request = event.detail && event.detail.request;
    if (!knownDestination(request) || suspended) return;
    if (!destination || request.country !== destination.country || request.province !== destination.province) quote = null;
    quoteRequest = request;
    quoteVersion = currentEpoch + ':' + currentRevision;
    rememberDestination(request);
  });
  document.addEventListener('rudca:shipping-quote-settled', event => {
    const detail = event.detail || {};
    if (suspended || waiting || document.documentElement.hasAttribute('data-rudca-cart-pending') || detail.request !== quoteRequest || quoteVersion !== currentEpoch + ':' + currentRevision) return;
    quoteRequest = null;
    if (detail.successful !== true || !Array.isArray(detail.rates) || !state || state.total < 6999 || !state.supported || state.currency !== 'USD') return;
    const freeStandard = detail.rates.some(rate => rate && typeof rate.name === 'string' && rate.name.trim().toLowerCase() === 'standard' && /^0(?:\.0+)?$/.test(String(rate.price)));
    quote = freeStandard ? null : {...state};
    rememberDestination(destination);
  });
  document.addEventListener('change', event => {
    if (!event.isTrusted || !event.target.matches('[name="country"], [name="province"]')) return;
    readDestination(event.target.closest('.shipping-estimator'));
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-action="estimate-shipping"]');
    if (button) readDestination(button.closest('.shipping-estimator'));
  }, true);
  document.addEventListener('input', event => {
    if (!restoringEstimator && event.target.closest(estimatorSelector) && event.target.matches('[name="country"], [name="province"], [name="zip"]')) captureEstimator();
  });
  document.addEventListener('change', event => {
    if (restoringEstimator || !event.target.closest(estimatorSelector)) return;
    if (event.isTrusted) captureEstimator();
    const host = estimatorHost();
    if (host) labelEstimator(host);
  });
  document.addEventListener('click', event => {
    const host = event.target.closest(estimatorSelector);
    if (!host) return;
    if (!mobileEstimator.matches) {
      if (event.target.closest('[data-action="toggle-collapsible"]')) queueMicrotask(() => renderEstimator(state));
      return;
    }
    const reopen = event.target.closest('[data-rudca-estimator-reopen]');
    const toggle = event.target.closest('[data-action="toggle-collapsible"]');
    if (!reopen && !toggle) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    estimatorChoice = reopen || !estimatorPanelOpen ? 'open' : 'closed';
    estimatorPanelOpen = estimatorChoice === 'open';
    // Explicit clicks work even while a cart update is pending.
    if (estimatorPanelOpen) estimatorCompact = false;
    renderEstimator(state);
    if (estimatorPanelOpen) host.querySelector('[name="province"]')?.focus({preventScroll: true});
    else if (estimatorCompact) host.querySelector('[data-rudca-estimator-reopen]')?.focus({preventScroll: true});
    else if (host.querySelector('#shipping-estimator')?.contains(document.activeElement)) toggle?.focus({preventScroll: true});
  }, true);
  document.addEventListener('cart:rerendered', () => {
    // Run after the native delegated CartSection handler and province helper.
    queueMicrotask(() => { restoreEstimator(); renderEstimator(state); });
  });
  document.addEventListener('shopify:section:load', () => {
    queueMicrotask(() => { restoreEstimator(); render(state); });
  });
  mobileEstimator.addEventListener('change', () => renderEstimator(state));
  function initializeEstimator() {
    // Restrict options before the existing province helper can restore a saved country.
    constrainEstimatorCountry();
    queueMicrotask(() => {
      captureEstimator();
      const previousCountry = normalize(destination?.country || document.querySelector(selector)?.dataset.destinationCountry).toUpperCase();
      if (estimatorAddress && previousCountry && !['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(previousCountry)) {
        quote = null;
        rememberDestination({country: estimatorAddress.country, province: estimatorAddress.province});
      } else renderEstimator(state);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeEstimator, {once: true});
  else initializeEstimator();
  window.addEventListener('pagehide', () => {
    suspended = true;
    currentEpoch += 1;
    currentRevision = -1;
    state = null;
    waiting = true;
    render(null);
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    suspended = false;
    state = null;
    waiting = true;
    render(null);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true});
  else initialize();
})();

(()=>{
 "use strict";
 const watched=new WeakSet();
 function measure(strip){
  const header=strip.parentElement;
  const nav=header?.querySelector(":scope > .nav-bar");
  if(!nav||!nav.getClientRects().length)return;
  const clearance=Math.max(0,strip.getBoundingClientRect().bottom-nav.getBoundingClientRect().bottom+(parseFloat(getComputedStyle(nav).borderBottomWidth)||0));
  const value=clearance+"px";
  if(header.style.getPropertyValue("--rudca-nav-shipping-clearance")!==value)header.style.setProperty("--rudca-nav-shipping-clearance",value);
 }
 const resize=new ResizeObserver(entries=>{for(const entry of entries){const header=entry.target.parentElement;const strip=header?.querySelector(":scope > [data-rudca-header-shipping]");if(strip)measure(strip);}});
 function initialize(){document.querySelectorAll("[data-rudca-header-shipping]").forEach(strip=>{measure(strip);if(watched.has(strip))return;const nav=strip.parentElement.querySelector(":scope > .nav-bar");if(!nav)return;watched.add(strip);resize.observe(strip);resize.observe(nav);});}
 if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",initialize,{once:true});else initialize();
 document.addEventListener("shopify:section:load",initialize);
})();
